<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-stethoscope me-2"></i>{{ t('triage.title','Triage Station') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="openArrival(false)">
            <i class="mdi mdi-account-plus me-1"></i>New Arrival
          </button>
          <button class="hms-btn hms-btn-warning" @click="openArrival(true)">
            <i class="mdi mdi-help-circle-outline me-1"></i>Unknown
          </button>
          <button class="hms-btn" :class="mci.mci_active == 1 ? 'hms-btn-danger-solid' : 'hms-btn-danger'" @click="toggleMci">
            <i class="mdi mdi-alert-octagon me-1"></i>{{ mci.mci_active == 1 ? 'MCI ACTIVE' : 'Activate MCI' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/ed-board')">
            <i class="mdi mdi-hospital me-1"></i>ED Board
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- MCI BANNER -->
      <div v-if="mci.mci_active == 1" class="hms-mci-banner mb-3">
        <i class="mdi mdi-alert-octagon"></i>
        <div class="flex-fill">
          <div class="hms-mci-title">MASS CASUALTY INCIDENT — {{ mci.mci_label || 'Active' }}</div>
          <div class="hms-mci-sub">Started {{ relativeTime(mci.mci_started_at) }} · {{ mci.live_mci_in_ed }} casualties currently in ED</div>
        </div>
        <button class="hms-btn hms-btn-warning" @click="showBulk = true"><i class="mdi mdi-account-multiple-plus me-1"></i>Bulk Register</button>
        <button class="hms-btn hms-btn-blue" @click="deactivateMci"><i class="mdi mdi-power me-1"></i>Stand Down</button>
      </div>

      <div class="row g-3">
        <!-- LEFT — Arrival Queue -->
        <div class="col-lg-4">
          <div class="hms-card">
            <div class="hms-section-label d-flex align-items-center justify-content-between">
              <span><i class="mdi mdi-clock-outline me-1"></i>AWAITING TRIAGE ({{ queue.length }})</span>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="callNextPatient" :disabled="callingNext" title="Call the next waiting patient in the triage queue">
                <i class="mdi mdi-bullhorn-outline me-1"></i>{{ callingNext ? 'Calling…' : 'Call next' }}
              </button>
            </div>

            <input class="hms-input mb-2" v-model="queueSearch"
                   placeholder="🔍 Search any patient by name / patient # / phone / ticket #"
                   @input="debouncedQueueSearch" />

            <!-- Date-range + time-range filter -->
            <div class="hms-triage-filter mb-2">
              <div class="d-flex gap-2 flex-wrap align-items-center">
                <label class="hms-triage-flbl">From</label>
                <input type="date" class="hms-input hms-triage-dt" v-model="filterFrom" :max="filterTo || todayIso" @change="loadAll" />
                <label class="hms-triage-flbl">To</label>
                <input type="date" class="hms-input hms-triage-dt" v-model="filterTo" :min="filterFrom" :max="todayIso" @change="loadAll" />
                <button type="button" class="hms-chip" :class="{ active: isTodayFilter }" @click="setFilterToday">Today</button>
                <button type="button" class="hms-chip" @click="setFilterYesterday">Yesterday</button>
                <button type="button" class="hms-chip" @click="setFilterWeek">Last 7d</button>
              </div>
              <div class="d-flex gap-2 flex-wrap align-items-center mt-1">
                <label class="hms-triage-flbl">Time</label>
                <input type="time" class="hms-input hms-triage-dt" v-model="filterTimeFrom" @change="loadAll" />
                <span class="hms-muted-sm">to</span>
                <input type="time" class="hms-input hms-triage-dt" v-model="filterTimeTo" @change="loadAll" />
                <button v-if="filterTimeFrom || filterTimeTo" type="button" class="hms-chip" @click="clearTimeFilter">Clear time</button>
              </div>
            </div>
            <div v-if="queueSearch" class="hms-muted-sm mb-2">
              {{ countVisits }} in ED · {{ countTickets }} in Casualty queue · {{ countPatients }} patient match{{ countPatients === 1 ? '' : 'es' }}
            </div>

            <div v-if="!queue.length" class="hms-empty-mini">
              <i class="mdi mdi-checkbox-marked-circle-outline"></i>{{ queueSearch ? 'No matches.' : 'Queue clear.' }}
            </div>

            <div v-else class="hms-queue-list">
              <div v-for="q in queue" :key="(q.source || 'v') + '-' + (q.visit_id || q.ticket_id || q.patient_id)"
                   class="hms-queue-item"
                   :class="[acuityBand(q), { active: selected && selected.visit_id && selected.visit_id == q.visit_id, ['src-' + (q.source || 'visit')]: true }]"
                   @click="pickQueueRow(q)">
                <div class="d-flex align-items-center gap-2">
                  <span class="hms-q-num hms-mono">
                    <span v-if="q.source === 'ticket'"><i class="mdi mdi-ticket-confirmation"></i> {{ q.ticket_number }}</span>
                    <span v-else-if="q.source === 'patient'"><i class="mdi mdi-account-search"></i> {{ q.patient_no }}</span>
                    <span v-else>{{ q.visit_number }}</span>
                  </span>
                  <span v-if="q.arrival_time" class="hms-q-time">
                    <i class="mdi mdi-timer-outline"></i> {{ minutesIn(q.arrival_time) }}m
                  </span>
                  <span v-if="q.source === 'ticket'" class="hms-badge hms-badge-info ms-auto">{{ (q.dept_name || 'Casualty').toUpperCase() }}</span>
                  <span v-else-if="q.source === 'patient'" class="hms-badge hms-badge-secondary ms-auto">REGISTER</span>
                  <span v-else-if="q.is_unknown == 1" class="hms-badge hms-badge-warning ms-auto">UNK</span>
                  <span v-if="q.mci_mode == 1" class="hms-badge hms-badge-danger ms-1">MCI</span>
                  <span v-if="q.ticket_priority && q.ticket_priority !== 'normal'" class="hms-badge hms-badge-danger ms-1">{{ q.ticket_priority.toUpperCase() }}</span>
                  <span v-if="isUnder5(q)" class="hms-badge hms-badge-danger ms-1" title="Patient is under 5 years — paediatric high alert"><i class="mdi mdi-baby-face me-1"></i>&lt; 5 yrs HIGH ALERT</span>
                </div>
                <div class="hms-q-name">{{ q.first_name }} {{ q.last_name }}</div>
                <div class="hms-muted-sm">{{ q.patient_no || '—' }} · {{ ageStr(q.dob) }} · {{ q.gender || '—' }}<span v-if="q.phone"> · {{ q.phone }}</span></div>
                <div class="hms-q-complaint" v-if="q.chief_complaint">"{{ truncate(q.chief_complaint, 60) }}"</div>
                <div v-if="hasRealAllergy(q.allergies)" class="hms-warn-strip"><i class="mdi mdi-alert-octagon"></i> {{ truncate(q.allergies, 50) }}</div>
                <div v-if="q.source === 'ticket'" class="hms-muted-sm hms-pullin"><i class="mdi mdi-arrow-right-bold"></i> Click to pull into triage</div>
                <div v-else-if="q.source === 'patient'" class="hms-muted-sm hms-pullin"><i class="mdi mdi-arrow-right-bold"></i> Click to register arrival &amp; triage</div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT — Triage Form -->
        <div class="col-lg-8">
          <div v-if="!selected" class="hms-card hms-empty">
            <i class="mdi mdi-account-arrow-left-outline"></i>
            Select a patient from the queue, or click <strong>New Arrival</strong> to register one.
          </div>

          <div v-else class="hms-card">
            <div class="d-flex align-items-center gap-3 mb-3">
              <div class="hms-avatar-lg">{{ initials }}</div>
              <div class="flex-fill">
                <div class="hms-h">
                  <span v-if="selected.is_unknown == 1" class="hms-badge hms-badge-warning me-1">UNKNOWN</span>
                  {{ selected.first_name }} {{ selected.last_name }}
                </div>
                <div class="hms-muted-sm">
                  <span class="hms-mono">{{ selected.patient_no || '—' }}</span> · {{ ageStr(selected.dob) }} · {{ selected.gender || '—' }} · arrived {{ minutesIn(selected.arrival_time) }}m ago
                </div>
                <div v-if="selected.chronic_conditions" class="hms-muted-sm"><i class="mdi mdi-pulse"></i> {{ selected.chronic_conditions }}</div>
                <!-- Allergies strip with inline edit -->
                <div v-if="!allergyEdit.open" class="hms-allergy-row mt-1">
                  <div v-if="hasRealAllergy(selected.allergies)" class="hms-warn-strip flex-grow-1">
                    <i class="mdi mdi-alert-octagon"></i> Allergies: {{ selected.allergies }}
                  </div>
                  <div v-else class="hms-allergy-none flex-grow-1">
                    <i class="mdi mdi-shield-check-outline"></i> No known allergies on file
                  </div>
                  <button v-if="selected.patient_id" class="hms-btn hms-btn-sm hms-btn-ghost ms-2" @click="openAllergyEdit" title="Update allergies">
                    <i class="mdi mdi-pencil-outline"></i>
                  </button>
                </div>
                <div v-else class="hms-allergy-editor mt-1">
                  <label class="hms-label"><i class="mdi mdi-alert-octagon me-1"></i>Update allergies</label>
                  <input class="hms-input" v-model="allergyEdit.value" placeholder="e.g. Penicillin, Sulpha drugs — separate with commas" @keyup.enter="saveAllergies" />
                  <label class="hms-label mt-2"><i class="mdi mdi-water me-1"></i>Blood group</label>
                  <select class="hms-input" v-model="allergyEdit.bloodGroup">
                    <option value="unknown">Unknown</option>
                    <option v-for="bg in ['A+','A-','B+','B-','AB+','AB-','O+','O-']" :key="bg" :value="bg">{{ bg }}</option>
                  </select>
                  <div class="hms-muted-sm mt-1">Common:
                    <span v-for="a in commonAllergyChips" :key="a" class="hms-chip hms-chip-sm ms-1" @click="addAllergyChip(a)">+ {{ a }}</span>
                  </div>
                  <div class="d-flex gap-2 mt-2">
                    <button class="hms-btn hms-btn-sm hms-btn-primary" @click="saveAllergies" :disabled="allergyEdit.busy">
                      <span v-if="allergyEdit.busy"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span>
                      <span v-else><i class="mdi mdi-check me-1"></i>Save</span>
                    </button>
                    <button class="hms-btn hms-btn-sm" @click="closeAllergyEdit">Cancel</button>
                    <button class="hms-btn hms-btn-sm hms-btn-ghost ms-auto" @click="markNoAllergies" :disabled="allergyEdit.busy" title="Set to No known allergies">
                      <i class="mdi mdi-shield-check-outline me-1"></i>No known allergies
                    </button>
                  </div>
                </div>
                <!-- Sharon F#3 #13 — patient consent OTP -->
                <div v-if="selected.patient_id" class="d-flex gap-2 flex-wrap mt-2">
                  <ConsentOtpButton :patient-id="selected.patient_id" category-code="hiv_art" category-label="HIV / ART" />
                  <ConsentOtpButton :patient-id="selected.patient_id" category-code="mental_health" category-label="Mental health" />
                </div>
              </div>
            </div>

            <div v-if="formError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ formError }}</div>

            <div class="hms-section-label">CHIEF COMPLAINT</div>
            <textarea class="hms-input mb-3" rows="2" v-model="form.chief_complaint" placeholder="Describe the chief complaint / add comments…"></textarea>

            <div class="hms-section-label">ACUITY ASSIGNMENT *</div>
            <div class="hms-acu-grid mb-3">
              <button v-for="lvl in acuityLevels" :key="lvl.code"
                      class="hms-acu-btn" :class="['acu-' + lvl.code, { selected: form.triage_level === lvl.code }]"
                      @click="form.triage_level = lvl.code">
                <div class="hms-acu-num">{{ lvl.short }}</div>
                <div class="hms-acu-lbl">{{ lvl.label }}</div>
                <div class="hms-acu-target">{{ lvl.target }}</div>
              </button>
            </div>

            <div class="hms-section-label">VITALS</div>
            <div class="row g-3 mb-3">
              <div class="col-md-3"><label class="hms-label">BP Systolic</label>
                <input type="number" class="hms-input" v-model.number="form.bp_systolic" placeholder="mmHg" />
              </div>
              <div class="col-md-3"><label class="hms-label">BP Diastolic</label>
                <input type="number" class="hms-input" v-model.number="form.bp_diastolic" placeholder="mmHg" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.pulse','Pulse') }}</label>
                <input type="number" class="hms-input" v-model.number="form.pulse" placeholder="bpm" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.rr','Respiratory Rate') }}</label>
                <input type="number" class="hms-input" v-model.number="form.respiratory_rate" placeholder="/min" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.temp','Temperature') }}</label>
                <input type="number" step="0.1" class="hms-input" v-model.number="form.temperature" placeholder="°C" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.spo2','SpO₂') }}</label>
                <input type="number" step="0.1" class="hms-input" v-model.number="form.oxygen_saturation" placeholder="%" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.weight','Weight') }}</label>
                <input type="number" step="0.1" class="hms-input" v-model.number="form.weight" placeholder="kg" />
              </div>
              <div class="col-md-3"><label class="hms-label">{{ t('triage.rbs','Blood Sugar') }}</label>
                <input type="number" step="0.1" class="hms-input" v-model.number="form.blood_sugar" placeholder="mmol/L" />
              </div>
              <div class="col-md-6"><label class="hms-label">{{ t('triage.news2','NEWS2 Score') }} (0–20)</label>
                <input type="number" class="hms-input" v-model.number="form.news2_score" min="0" max="20" />
                <div class="hms-muted-sm mt-1">Suggested: {{ news2Suggestion }}</div>
              </div>
              <div class="col-md-6"><label class="hms-label">{{ t('triage.pain','Pain Score (0–10)') }}</label>
                <input type="range" class="hms-range" v-model.number="form.pain_score" min="0" max="10" />
                <div class="d-flex justify-content-between hms-muted-sm">
                  <span>0 None</span><span class="fw-semibold">{{ form.pain_score }}/10</span><span>10 Worst</span>
                </div>
              </div>
            </div>

            <div class="hms-section-label">ASSIGN BAY (optional)</div>
            <select class="hms-input mb-3" v-model="form.bay_id">
              <option value="">— Skip (unassigned) —</option>
              <optgroup v-for="grp in availableBaysGrouped" :key="grp.type" :label="formatBayType(grp.type)">
                <option v-for="b in grp.bays" :key="b.bay_id" :value="b.bay_id">{{ b.bay_name }}</option>
              </optgroup>
            </select>

            <div class="hms-section-label">TRIAGE NOTES</div>
            <textarea class="hms-input mb-3" rows="3" v-model="form.triage_notes" placeholder="Brief observations, mechanism of injury, key red flags..."></textarea>

            <div class="d-flex gap-2 align-items-center flex-wrap">
              <button class="hms-btn hms-btn-amber" @click="selected = null">Cancel</button>
              <SaveAndQueue ref="sqTriage" from-point="triage" :saving="processing" :disabled="!form.triage_level"
                            save-label="Complete Triage" saving-label="Saving..."
                            btn-class="hms-btn hms-btn-primary" icon="mdi-check" @save="submitTriage" />
            </div>
          </div>
        </div>
      </div>

      <!-- ARRIVAL MODAL -->
      <div class="hms-modal-overlay" v-if="showArrival" @click.self="showArrival=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="arrivalForm.is_unknown ? 'mdi-help-circle-outline' : 'mdi-account-plus'"></i>
              {{ arrivalForm.is_unknown ? 'Unknown / MCI Arrival' : 'New Emergency Arrival' }}
            </span>
            <button class="hms-modal-close" @click="showArrival=false">&times;</button>
          </div>
          <div v-if="arrivalError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ arrivalError }}</div>

          <div v-if="!arrivalForm.is_unknown" class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Existing patient? <a href="javascript:void(0)" @click="searchExisting=!searchExisting">{{ searchExisting ? 'Hide search' : 'Search by phone / patient #' }}</a>
          </div>

          <div v-if="!arrivalForm.is_unknown && searchExisting" class="mb-3">
            <input class="hms-input" v-model="patientSearch" placeholder="Type to search..." @input="debouncedPatientSearch" />
            <div v-if="patientResults.length" class="hms-search-results">
              <div v-for="p in patientResults" :key="p.patient_id" class="hms-search-result" @click="pickExistingPatient(p)">
                <div class="hms-text fw-semibold">{{ p.first_name }} {{ p.last_name }}</div>
                <div class="hms-muted-sm">{{ p.patient_no }} · {{ p.phone || '—' }} · {{ ageStr(p.dob) }}</div>
              </div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div v-if="!arrivalForm.is_unknown" class="col-md-6"><label class="hms-label">First Name *</label>
              <input class="hms-input" v-model="arrivalForm.first_name" :disabled="!!arrivalForm.patient_id" />
            </div>
            <div v-if="!arrivalForm.is_unknown" class="col-md-6"><label class="hms-label">Last Name</label>
              <input class="hms-input" v-model="arrivalForm.last_name" :disabled="!!arrivalForm.patient_id" />
            </div>
            <div class="col-md-4"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="arrivalForm.gender" :disabled="!!arrivalForm.patient_id">
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other / Unknown</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Approx. Age (years)</label>
              <input type="number" class="hms-input" v-model.number="arrivalForm.approx_age" min="0" :disabled="!!arrivalForm.patient_id" />
            </div>
            <div v-if="!arrivalForm.is_unknown" class="col-md-4"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="arrivalForm.phone" :disabled="!!arrivalForm.patient_id" />
            </div>
            <div class="col-12"><label class="hms-label">Chief Complaint / Mechanism *</label>
              <textarea class="hms-input" rows="2" v-model="arrivalForm.chief_complaint" placeholder="e.g. RTA, head-on collision, GCS 13" required></textarea>
            </div>
            <div class="col-md-6"><label class="hms-label">Arrival Source</label>
              <select class="hms-input" v-model="arrivalForm.arrival_source">
                <option value="walk_in">Walk-in</option>
                <option value="ambulance">Ambulance</option>
                <option value="referral">Referral (from satellite)</option>
                <option value="police">Police</option>
              </select>
            </div>
            <div class="col-md-6 d-flex align-items-end">
              <label class="hms-toggle">
                <input type="checkbox" :checked="arrivalForm.mci_mode == 1" @change="arrivalForm.mci_mode = $event.target.checked ? 1 : 0" />
                <span>Mass Casualty Mode (MCI)</span>
              </label>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitArrival" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Registering...' : 'Register Arrival → Send to Triage' }}
          </button>
        </div>
      </div>

      <!-- MCI ACTIVATE MODAL -->
      <div class="hms-modal-overlay" v-if="showActivateMci" @click.self="showActivateMci=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-alert-octagon text-danger me-1"></i>Activate MCI Mode</span>
            <button class="hms-modal-close" @click="showActivateMci=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Activating MCI mode flags this hospital for mass-casualty operations. Bulk registration will be enabled and all incoming patients can be tagged as MCI casualties.
          </div>
          <label class="hms-label">Incident Label *</label>
          <input class="hms-input mb-3" v-model="mciLabel" placeholder="e.g. Narok–Kisii bus RTA, 28 casualties" />
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showActivateMci=false">Cancel</button>
            <button class="hms-btn hms-btn-danger flex-fill" @click="activateMci" :disabled="processing || !mciLabel">Activate MCI</button>
          </div>
        </div>
      </div>

      <!-- MCI BULK REGISTER MODAL -->
      <div class="hms-modal-overlay" v-if="showBulk" @click.self="showBulk=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-multiple-plus me-1"></i>MCI Bulk Register</span>
            <button class="hms-modal-close" @click="showBulk=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Registers up to 50 unidentified casualties at once. Each gets <strong>"Unknown Male/Female #NNN"</strong> placeholder name and an emergency visit ready for triage.
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Number of casualties *</label>
              <input type="number" class="hms-input" v-model.number="bulk.count" min="1" max="50" />
            </div>
            <div class="col-md-4"><label class="hms-label">Sex split</label>
              <select class="hms-input" v-model="bulk.gender_split">
                <option value="mixed">Mixed (alternating)</option>
                <option value="male">All male</option>
                <option value="female">All female</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Default complaint</label>
              <input class="hms-input" v-model="bulk.chief_complaint" placeholder="MCI casualty — pending triage" />
            </div>
          </div>
          <div class="hms-info-banner mb-3" style="background: rgba(220,53,69,0.08); color: var(--hms-danger); border-color: rgba(220,53,69,0.3);">
            <i class="mdi mdi-alert me-1"></i>
            They will appear in the triage queue immediately. Use the triage form to assign acuity and identify them later.
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showBulk=false">Cancel</button>
            <button class="hms-btn hms-btn-danger flex-fill" @click="confirmBulk" :disabled="processing || bulk.count < 1">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
              Register {{ bulk.count }} casualties
            </button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#1a7fe8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#0047AB;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-text { color: var(--hms-h); }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-h { color: var(--hms-h); font-size: 16px; font-weight: 700; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 4px; margin-bottom: 8px; }

.hms-empty { text-align: center; padding: 60px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }
.hms-empty-mini { text-align: center; padding: 20px; color: var(--hms-s); font-size: 13px; }
.hms-empty-mini i { font-size: 32px; color: var(--hms-success); display: block; margin-bottom: 8px; }

.hms-queue-list { display: flex; flex-direction: column; gap: 6px; max-height: 700px; overflow-y: auto; }
.hms-queue-item { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 12px; cursor: pointer; transition: all .15s; }
.hms-queue-item:hover { border-color: var(--hms-primary); }
.hms-queue-item.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-q-num { color: var(--hms-s); }
.hms-q-time { font-size: 11px; color: var(--hms-h); padding: 1px 8px; border: 1px solid var(--hms-br); border-radius: 50px; }
.hms-q-name { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-top: 4px; }
.hms-q-complaint { color: var(--hms-s); font-style: italic; font-size: 12px; margin-top: 4px; }

.hms-warn-strip { background: rgba(220,53,69,.08); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.25); border-radius: 6px; padding: 4px 8px; font-size: 12px; }
.hms-warn-strip i { margin-right: 4px; }

.hms-avatar-lg { width: 56px; height: 56px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; flex-shrink: 0; }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-m); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-range { width: 100%; accent-color: var(--hms-primary); }
.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-search-results { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; margin-top: 6px; max-height: 220px; overflow-y: auto; }
.hms-search-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-search-result:last-child { border-bottom: none; }
.hms-search-result:hover { background: var(--hms-primary-lt); }

.hms-acu-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.hms-acu-btn { background: var(--hms-bg2); border: 2px solid var(--hms-br); border-radius: 10px; padding: 14px 8px; cursor: pointer; transition: all .15s; text-align: center; }
.hms-acu-btn:hover { transform: translateY(-2px); }
.hms-acu-btn.acu-1_immediate.selected   { border-color: var(--acu-1); background: rgba(220,53,69,.12); color: var(--acu-1); }
.hms-acu-btn.acu-2_emergent.selected    { border-color: var(--acu-2); background: rgba(253,126,20,.12); color: var(--acu-2); }
.hms-acu-btn.acu-3_urgent.selected      { border-color: var(--acu-3); background: rgba(255,193,7,.12); color: #b88600; }
.hms-acu-btn.acu-4_less_urgent.selected { border-color: var(--acu-4); background: rgba(40,167,69,.12); color: var(--acu-4); }
.hms-acu-btn.acu-5_non_urgent.selected  { border-color: var(--acu-5); background: var(--hms-primary-lt); color: var(--hms-primary); }

/* Queue row acuity colour bands (red = critical) */
.hms-queue-item.acu-band-1 { border-left: 4px solid var(--acu-1); }
.hms-queue-item.acu-band-2 { border-left: 4px solid var(--acu-2); }
.hms-queue-item.acu-band-3 { border-left: 4px solid var(--acu-3); }
.hms-queue-item.acu-band-4 { border-left: 4px solid var(--acu-4); }
.hms-queue-item.acu-band-5 { border-left: 4px solid var(--acu-5); }
.hms-acu-num { font-size: 22px; font-weight: 800; }
.hms-acu-lbl { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.hms-acu-target { font-size: 10px; color: var(--hms-s); margin-top: 2px; }

.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-warning   { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger    { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-info      { background: rgba(80,165,241,.15); color: #2c5282; }
.hms-badge-secondary { background: rgba(148,163,184,.18); color: #475569; }

.hms-queue-item.src-ticket  { border-left: 3px solid #50a5f1; }
.hms-queue-item.src-patient { border-left: 3px solid #94a3b8; }
.hms-pullin { color: #2c5282; font-weight: 600; margin-top: 4px; }

/* MCI banner + button */
.text-danger { color: var(--hms-danger); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-danger-solid { background: var(--hms-danger); color: #fff; border: 1px solid var(--hms-danger); animation: hms-pulse 1.4s infinite; }
@keyframes hms-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,53,69,.6); } 50% { box-shadow: 0 0 0 10px rgba(220,53,69,0); } }
.hms-mci-banner { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; background: linear-gradient(90deg, rgba(220,53,69,0.20), rgba(220,53,69,0.06)); border: 2px solid var(--hms-danger); border-radius: 12px; padding: 14px 18px; }
.hms-mci-banner > i { font-size: 32px; color: var(--hms-danger); flex-shrink: 0; }
.hms-mci-title { color: var(--hms-danger); font-weight: 800; font-size: 14px; letter-spacing: 1.5px; }
.hms-mci-sub   { color: var(--hms-h); font-size: 12px; margin-top: 2px; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-warning { background: rgba(255,193,7,.15); color: #ca8a04; border: 1px solid rgba(255,193,7,.3); }
.hms-btn-warning:hover:not(:disabled) { background: rgba(255,193,7,.25); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner a { color: var(--hms-primary); text-decoration: underline; font-weight: 600; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* Inline allergy editor */
.hms-allergy-row { display: flex; align-items: center; gap: 6px; }
.hms-allergy-none { padding: 4px 10px; border-radius: 6px; background: rgba(40,167,69,.08); color: var(--hms-success); font-size: 12px; }
.hms-allergy-editor { background: var(--hms-bg2); border: 1px solid var(--hms-warning); border-radius: 8px; padding: 10px 12px; }
.hms-btn-ghost { background: transparent; color: var(--hms-s); border: 1px solid var(--hms-br); }
.hms-btn-ghost:hover { background: var(--hms-bg3); color: var(--hms-h); }
.hms-chip-sm { padding: 2px 8px; font-size: 10.5px; border-radius: 10px; background: var(--hms-bg3); color: var(--hms-h); border: 1px solid var(--hms-br); cursor: pointer; display: inline-block; margin-top: 2px; }
.hms-chip-sm:hover { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import ConsentOtpButton from "@/components/hms-shared/ConsentOtpButton.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

const ACUITY = [
  { code: '1_immediate',    short: 'L1', label: 'Immediate', target: '0 min' },
  { code: '2_emergent',     short: 'L2', label: 'Emergent',  target: '<10 min' },
  { code: '3_urgent',       short: 'L3', label: 'Urgent',    target: '<60 min' },
  { code: '4_less_urgent',  short: 'L4', label: 'Less Urgent',target: '<120 min' },
  { code: '5_non_urgent',   short: 'L5', label: 'Non Urgent', target: '<240 min' }
];

export default {
  name: "TriageStation",
  components: { Navigation, SaveAndQueue, ConsentOtpButton },
  data() {
    return {
      loading: true, processing: false, callingNext: false,
      queue: [], bays: [],
      queueSearch: '', _queueSearchTimer: null,
      countVisits: 0, countTickets: 0, countPatients: 0,
      // Date/time filter — defaults to TODAY only
      filterFrom: new Date().toISOString().slice(0,10),
      filterTo:   new Date().toISOString().slice(0,10),
      filterTimeFrom: '', filterTimeTo: '',
      allergyEdit: { open: false, value: '', bloodGroup: 'unknown', busy: false },
      commonAllergyChips: ['Penicillin','Sulpha drugs','NSAIDs','Aspirin','Cephalosporins','Iodine','Latex','Peanuts','Shellfish','No known allergies'],
      i18n: useI18n(),

      selected: null,
      form: this.emptyForm(),
      formError: '',

      showArrival: false, arrivalError: '',
      arrivalForm: this.emptyArrival(),

      searchExisting: false,
      patientSearch: '',
      patientResults: [],
      _searchTimer: null,

      acuityLevels: ACUITY,

      // MCI state
      mci: { mci_active: 0, mci_label: '', mci_started_at: null, live_mci_in_ed: 0 },
      showActivateMci: false, mciLabel: '',
      showBulk: false,
      bulk: { count: 5, gender_split: 'mixed', chief_complaint: 'MCI casualty — pending triage' },

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    todayIso() { return new Date().toISOString().slice(0, 10); },
    isTodayFilter() {
      return this.filterFrom === this.todayIso && this.filterTo === this.todayIso;
    },
    initials() {
      if (!this.selected) return '?';
      var f = (this.selected.first_name || '').charAt(0);
      var l = (this.selected.last_name  || '').charAt(0);
      return ((f + l).toUpperCase()) || '?';
    },
    availableBaysGrouped() {
      var by = {};
      (this.bays || []).filter(b => b.status === 'available').forEach(b => {
        if (!by[b.bay_type]) by[b.bay_type] = [];
        by[b.bay_type].push(b);
      });
      return Object.keys(by).map(t => ({ type: t, bays: by[t] }));
    },
    news2Suggestion() {
      // Compute a rough NEWS2 suggestion from entered vitals
      var f = this.form;
      var s = 0;
      if (f.respiratory_rate <= 8 || f.respiratory_rate >= 25) s += 3;
      else if (f.respiratory_rate >= 21) s += 2;
      else if (f.respiratory_rate >= 9 && f.respiratory_rate <= 11) s += 1;

      if (f.oxygen_saturation && f.oxygen_saturation < 92) s += 3;
      else if (f.oxygen_saturation && f.oxygen_saturation < 94) s += 2;
      else if (f.oxygen_saturation && f.oxygen_saturation < 96) s += 1;

      if (f.temperature && (f.temperature <= 35 || f.temperature >= 39.1)) s += 3;
      else if (f.temperature && (f.temperature >= 38.1 || f.temperature <= 36)) s += 1;

      if (f.bp_systolic && (f.bp_systolic <= 90 || f.bp_systolic >= 220)) s += 3;
      else if (f.bp_systolic && f.bp_systolic <= 100) s += 2;
      else if (f.bp_systolic && f.bp_systolic <= 110) s += 1;

      if (f.pulse && (f.pulse <= 40 || f.pulse >= 131)) s += 3;
      else if (f.pulse && (f.pulse >= 111)) s += 2;
      else if (f.pulse && (f.pulse >= 91 || f.pulse <= 50)) s += 1;

      return s;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
    this.loadMci();
    this._mciTimer = setInterval(() => this.loadMci(), 60000);
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this._mciTimer) clearInterval(this._mciTimer);
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },
    emptyForm() {
      return { triage_level: '', bay_id: '', triage_notes: '', chief_complaint: '',
        bp_systolic: 0, bp_diastolic: 0, pulse: 0, respiratory_rate: 0,
        temperature: 0, oxygen_saturation: 0, weight: 0, height: 0,
        blood_sugar: 0, news2_score: 0, pain_score: 0 };
    },
    emptyArrival() {
      return { patient_id: '', is_unknown: 0, first_name: '', last_name: '', gender: 'male',
        approx_age: 0, phone: '', chief_complaint: '', mci_mode: 0, arrival_source: 'walk_in' };
    },

    // "No known allergies" / "none" / "n/a" is not an alert-worthy allergy.
    // Filter these out so the warning strip only shows real allergens.
    hasRealAllergy(v) {
      if (!v) return false;
      var t = String(v).trim().toLowerCase();
      if (!t) return false;
      var noise = ['no known allergies','nka','none','n/a','nil','none known','no allergies','no known allergy'];
      return noise.indexOf(t) === -1;
    },

    // Inline-edit allergies on the selected patient
    openAllergyEdit() {
      this.allergyEdit.value = (this.selected && this.selected.allergies) || '';
      this.allergyEdit.bloodGroup = (this.selected && this.selected.blood_group) || 'unknown';
      this.allergyEdit.busy = false;
      this.allergyEdit.open = true;
    },
    closeAllergyEdit() {
      this.allergyEdit.open = false;
    },
    addAllergyChip(a) {
      var cur = (this.allergyEdit.value || '').trim();
      // Avoid duplicates
      var parts = cur ? cur.split(',').map(function(s){ return s.trim().toLowerCase(); }) : [];
      if (parts.indexOf(a.toLowerCase()) !== -1) return;
      this.allergyEdit.value = cur ? cur + ', ' + a : a;
    },
    markNoAllergies() {
      this.allergyEdit.value = 'No known allergies';
      this.saveAllergies();
    },
    async saveAllergies() {
      if (!this.selected || !this.selected.patient_id) return;
      this.allergyEdit.busy = true;
      try {
        var val = (this.allergyEdit.value || '').trim();
        var r = await executeJson({
          function: 'updatePatient',
          patient_id: this.selected.patient_id,
          allergies: val,
          blood_group: this.allergyEdit.bloodGroup || 'unknown'
        }, hms);
        if (r.data && r.data.success) {
          // Update local mirror on both the selected panel and the queue row
          this.selected.allergies = val;
          this.selected.blood_group = this.allergyEdit.bloodGroup || 'unknown';
          var self = this;
          this.queue.forEach(function(q) {
            if (q.patient_id && q.patient_id === self.selected.patient_id) q.allergies = val;
          });
          this.showToast('Allergies updated');
          this.allergyEdit.open = false;
        } else {
          this.showToast((r.data && r.data.message) || 'Update failed', true);
        }
      } catch (e) {
        this.showToast('Network error', true);
      } finally {
        this.allergyEdit.busy = false;
      }
    },

    // Quick date-range presets for the awaiting-triage filter
    setFilterToday() {
      var t = this.todayIso;
      this.filterFrom = t; this.filterTo = t;
      this.loadAll();
    },
    setFilterYesterday() {
      var d = new Date(); d.setDate(d.getDate() - 1);
      var y = d.toISOString().slice(0, 10);
      this.filterFrom = y; this.filterTo = y;
      this.loadAll();
    },
    setFilterWeek() {
      var end = new Date(); var start = new Date(); start.setDate(end.getDate() - 6);
      this.filterFrom = start.toISOString().slice(0, 10);
      this.filterTo   = end.toISOString().slice(0, 10);
      this.loadAll();
    },
    clearTimeFilter() {
      this.filterTimeFrom = ''; this.filterTimeTo = '';
      this.loadAll();
    },

    async loadAll() {
      this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({
            function:   "getTriageQueue",
            search:     this.queueSearch || '',
            date_from:  this.filterFrom || '',
            date_to:    this.filterTo || '',
            time_from:  this.filterTimeFrom || '',
            time_to:    this.filterTimeTo || ''
          }, hms),
          executeJson({ function: "getResusBays" }, hms)
        ]);
        if (res[0].data && res[0].data.success) {
          var d = res[0].data.data || {};
          this.queue         = d.Queue || [];
          this.countVisits   = d.CountVisits   || 0;
          this.countTickets  = d.CountTickets  || 0;
          this.countPatients = d.CountPatients || 0;
        }
        if (res[1].data && res[1].data.success) this.bays = (res[1].data.data && res[1].data.data.Bays) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    debouncedQueueSearch() {
      if (this._queueSearchTimer) clearTimeout(this._queueSearchTimer);
      var self = this;
      this._queueSearchTimer = setTimeout(() => self.loadAll(), 300);
    },
    async callNextPatient() {
      this.callingNext = true;
      try {
        var r = await executeJson({ function: "callNextForModule", module: "triage" }, hms);
        if (r.data && r.data.success) {
          var t = r.data.data && r.data.data.Ticket;
          this.showToast(r.data.message || 'Next patient called');
          await this.loadAll();
          if (t && t.patient_id) {
            var row = this.queue.find(function(q){ return String(q.patient_id) === String(t.patient_id); });
            if (row) this.pickQueueRow(row);
          }
        } else {
          this.showToast((r.data && r.data.message) || 'No waiting patients', true);
        }
      } catch (e) { this.showToast('Failed to call next patient', true); }
      this.callingNext = false;
    },

    selectVisit(v) {
      this.selected = v;
      this.form = this.emptyForm();
      this.form.chief_complaint = v.chief_complaint || '';
      this.formError = '';
    },

    async pickQueueRow(q) {
      // Existing emergency visit (has a visit number) — select directly
      if ((q.source === 'visit' || !q.source) && q.visit_id && q.visit_number) {
        this.selectVisit(q);
        return;
      }

      // Ticket or patient row — register an emergency arrival linked to that patient,
      // then build the selected object from the response and the row data.
      if (this.processing) return;
      if (!q.patient_id) { this.showToast("This row has no patient — use New Arrival", true); return; }

      var label = q.source === 'ticket' ? 'queue ticket ' + (q.ticket_number || '') : 'patient ' + (q.patient_no || '');

      // Populate the selected patient on the right immediately so the user always sees
      // who they clicked, even if they cancel the "create emergency visit" prompt.
      // (Sharon F#: clicking a row should show the patient details either way.)
      var stubChief = q.chief_complaint || (q.source === 'ticket'
        ? 'Sent from ' + (q.dept_name || 'queue') + ' for triage'
        : 'Walk-in for triage');
      this.selected = {
        source: q.source || 'patient',
        visit_id: null,
        visit_number: q.ticket_number || q.patient_no || null,
        patient_id: q.patient_id,
        first_name: q.first_name, last_name: q.last_name,
        patient_no: q.patient_no, dob: q.dob, gender: q.gender, phone: q.phone,
        allergies: q.allergies, chronic_conditions: q.chronic_conditions,
        chief_complaint: stubChief,
        arrival_time: q.arrival_time || new Date().toISOString().replace('T',' ').substring(0,19),
        is_unknown: 0, mci_mode: 0,
        _needs_visit: true   // form save will create the visit if not yet created
      };
      this.form = this.emptyForm();
      this.form.chief_complaint = stubChief;
      this.formError = '';

      // No confirm popup — every click flows straight into the normal triage process.
      this.processing = true;
      try {
        var payload = {
          function: "registerEmergencyArrival",
          patient_id: q.patient_id,
          chief_complaint: q.chief_complaint || (q.source === 'ticket' ? 'Sent from ' + (q.dept_name || 'queue') + ' for triage' : 'Walk-in for triage'),
          arrival_source: 'walk_in'
        };
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          // Build selected object so the form opens immediately
          this.selected = {
            source: 'visit',
            visit_id: d.visit_id,
            visit_number: d.visit_number,
            patient_id: q.patient_id,
            first_name: q.first_name,
            last_name: q.last_name,
            patient_no: q.patient_no,
            dob: q.dob,
            gender: q.gender,
            phone: q.phone,
            allergies: q.allergies,
            chronic_conditions: q.chronic_conditions,
            chief_complaint: payload.chief_complaint,
            arrival_time: new Date().toISOString().replace('T',' ').substring(0,19),
            is_unknown: 0,
            mci_mode: 0
          };
          this.form = this.emptyForm();
          this.form.chief_complaint = payload.chief_complaint || '';
          this.formError = '';
          // Clear the search so this new visit appears in the queue too
          this.queueSearch = '';
          this.showToast("Visit " + d.visit_number + " created — proceed with triage");
          await this.loadAll();
        } else {
          this.showToast((r.data && r.data.message) || "Could not register arrival", true);
        }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    minutesIn(arrival) {
      if (!arrival) return 0;
      var d = new Date((arrival || '').replace ? arrival.replace(' ','T') : arrival);
      if (isNaN(d)) return 0;
      return Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    },
    ageStr(dob) {
      if (!dob || dob === '0000-00-00' || dob === '1900-01-01') return 'age unknown';
      var d = new Date(dob); if (isNaN(d)) return '—';
      var diff = Date.now() - d.getTime();
      var years = Math.floor(diff / (365.25 * 24 * 3600000));
      return years + 'y';
    },
    truncate(s, n) { return (s || '').length > n ? s.substring(0,n) + '...' : (s || ''); },
    formatBayType(t) { return ({ resus:'Resus', cubicle:'Cubicle', observation:'Observation', triage:'Triage', isolation:'Isolation' })[t] || t; },
    isUnder5(q) {
      if (!q || !q.dob) return false;
      var d = new Date(q.dob); if (isNaN(d)) return false;
      var t = new Date();
      var years = t.getFullYear() - d.getFullYear();
      var m = t.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && t.getDate() < d.getDate())) years--;
      return years < 5;
    },
    acuityBand(q) {
      // Left colour band by acuity (post-triage) or by ticket priority (pre-triage).
      var lvl = (q.triage_level || '').toString();
      if (lvl) {
        var n = lvl.charAt(0);
        if (n >= '1' && n <= '5') return 'acu-band-' + n;
      }
      var p = q.ticket_priority || '';
      if (p === 'emergency') return 'acu-band-1';
      if (p === 'elderly_disabled') return 'acu-band-2';
      if (p === 'urgent') return 'acu-band-3';
      // Paediatric high alert: under-5 patients get a red band if no clinical acuity is yet assigned
      if (this.isUnder5(q)) return 'acu-band-1';
      return '';
    },

    async submitTriage() {
      this.formError = '';
      if (!this.form.triage_level) { this.formError = 'Acuity level is required.'; return; }
      this.processing = true;
      try {
        var payload = Object.assign({ function: "completeTriage", visit_id: this.selected.visit_id }, this.form);
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Triage saved · Patient on ED Board");
          // If triage nurse picked a "Save & queue to ___", route the patient now.
          var pid = this.selected && this.selected.patient_id;
          if (this.$refs.sqTriage && pid) {
            var q = await this.$refs.sqTriage.queueIfSelected(pid);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          this.selected = null;
          await this.loadAll();
        } else { this.formError = r.data.message || "Failed."; }
      } catch(e) { this.formError = "Connection error."; }
      this.processing = false;
    },

    openArrival(isUnknown) {
      this.arrivalForm = this.emptyArrival();
      this.arrivalForm.is_unknown = isUnknown ? 1 : 0;
      this.arrivalError = '';
      this.searchExisting = false;
      this.patientSearch = '';
      this.patientResults = [];
      this.showArrival = true;
    },
    debouncedPatientSearch() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(async () => {
        if (!self.patientSearch || self.patientSearch.length < 2) { self.patientResults = []; return; }
        try {
          var r = await executeJson({ function: "getPatients", search: self.patientSearch }, hms);
          if (r.data && r.data.success) self.patientResults = ((r.data.data && r.data.data.Patients) || []).slice(0, 8);
        } catch(e) {}
      }, 300);
    },
    pickExistingPatient(p) {
      this.arrivalForm.patient_id = p.patient_id;
      this.arrivalForm.first_name = p.first_name || '';
      this.arrivalForm.last_name  = p.last_name || '';
      this.arrivalForm.gender     = p.gender || 'male';
      this.arrivalForm.phone      = p.phone || '';
      if (p.dob) {
        var d = new Date(p.dob);
        if (!isNaN(d)) this.arrivalForm.approx_age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600000));
      }
      this.patientResults = [];
      this.patientSearch = '';
      this.searchExisting = false;
    },

    // ---------- MCI ----------
    async loadMci() {
      try {
        var r = await executeJson({ function: "getMciStatus" }, hms);
        if (r.data && r.data.success) this.mci = (r.data.data && r.data.data.Mci) || this.mci;
      } catch(e) {}
    },
    toggleMci() {
      if (this.mci.mci_active == 1) {
        if (confirm("Stand down MCI mode?\n\nThis ends mass-casualty operations. Already-registered MCI patients keep their flag.")) this.deactivateMci();
      } else {
        this.mciLabel = '';
        this.showActivateMci = true;
      }
    },
    async activateMci() {
      if (!this.mciLabel) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "activateMci", label: this.mciLabel }, hms);
        if (r.data && r.data.success) {
          this.showActivateMci = false; this.showToast("MCI ACTIVATED");
          await this.loadMci();
        } else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async deactivateMci() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "deactivateMci" }, hms);
        if (r.data && r.data.success) { this.showToast("MCI stood down"); await this.loadMci(); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async confirmBulk() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "bulkRegisterMciArrivals" }, this.bulk), hms);
        if (r.data && r.data.success) {
          var n = (r.data.data && r.data.data.created || []).length;
          this.showToast(n + " casualties registered · check triage queue");
          this.showBulk = false;
          await this.loadAll();
          await this.loadMci();
        } else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    relativeTime(s) {
      if (!s) return ''; var d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return s;
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return Math.floor(diff) + 's ago';
      if (diff < 3600) return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      return Math.floor(diff/86400) + 'd ago';
    },

    async submitArrival() {
      this.arrivalError = '';
      if (!this.arrivalForm.is_unknown && !this.arrivalForm.patient_id && !this.arrivalForm.first_name) {
        this.arrivalError = 'First name is required (or mark as Unknown).'; return;
      }
      if (!this.arrivalForm.chief_complaint) { this.arrivalError = 'Chief complaint is required.'; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "registerEmergencyArrival" }, this.arrivalForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Arrival registered · Now in triage queue");
          this.showArrival = false;
          await this.loadAll();
          // Auto-select the new arrival
          var nv = this.queue.find(q => q.visit_id == r.data.data.visit_id);
          if (nv) this.selectVisit(nv);
        } else { this.arrivalError = r.data.message || 'Failed.'; }
      } catch(e) { this.arrivalError = 'Connection error.'; }
      this.processing = false;
    }
  }
};
</script>
